import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Loader2, RotateCcw, Pencil, Sparkles, Volume2, Mic } from "lucide-react";
import type { Character, Message } from "../services/api";
import { sendMessage, sendMessageStream, parseSSEStream } from "../services/api";
import { TTSButton } from "./TTSButton";
import { AudioInput } from "./AudioInput";
import { TranslateButton } from "./TranslateButton";

interface ChatViewProps {
  character: Character;
  conversationId?: string;
  onBack: () => void;
  onEditCharacter?: (char: Character) => void;
  onNewConversation?: (characterId: string) => void;
}

export function ChatView({ character, conversationId: initialConvId, onBack, onEditCharacter, onNewConversation }: ChatViewProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [convId, setConvId] = useState(initialConvId || "");
  const [streamingReply, setStreamingReply] = useState("");
  const [thinkingStatus, setThinkingStatus] = useState("");
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isStreaming = useRef(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Sync initialConvId prop to convId state when prop changes
  useEffect(() => {
    if (initialConvId && initialConvId !== convId) {
      setConvId(initialConvId);
      setMessages([]);
      setInput("");
      setStreamingReply("");
      setIsLoading(false);
      isStreaming.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConvId]);

  // Initialize conversation
  useEffect(() => {
    if (convId) {
      loadHistory();
    } else {
      // New conversation: show greeting + track guest count
      if (character.greeting) {
        const greetingMsg: Message = {
          id: `greeting_${Date.now()}`,
          role: "assistant",
          content: character.greeting,
          timestamp: new Date().toISOString(),
        };
        setMessages([greetingMsg]);
      }
      // 游客模式：通知父组件新对话开始
      window.dispatchEvent(new CustomEvent("guest-conversation-started", { detail: { characterId: character.id } }));
    }
    setShowRestartConfirm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convId]);

  async function loadHistory() {
    try {
      const res = await fetch(`http://localhost:8765/api/conversations/${convId}`);
      const data = await res.json();
      if (data.success && data.data?.messages) {
        setMessages(data.data.messages);
      }
    } catch (e) {
      console.error("加载历史失败:", e);
    }
  }

  // Restart conversation - clear and start fresh
  function handleRestart() {
    if (isLoading || isStreaming.current) return;
    setConvId("");
    setMessages([]);
    setInput("");
    setStreamingReply("");
    setShowRestartConfirm(false);
    if (onNewConversation) {
      onNewConversation(character.id);
    }
    // Show greeting after state reset
    setTimeout(() => {
      if (character.greeting) {
        const greetingMsg: Message = {
          id: `greeting_${Date.now()}`,
          role: "assistant",
          content: character.greeting,
          timestamp: new Date().toISOString(),
        };
        setMessages([greetingMsg]);
      }
    }, 0);
  }

  async function handleSend() {
    if (!input.trim() || isLoading) return;

    const userMsg = input.trim();
    setInput("");
    setIsLoading(true);
    setStreamingReply("");
    setThinkingStatus("");

    const userMessage: Message = {
      id: `msg_${Date.now()}`,
      role: "user",
      content: userMsg,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      // Try streaming first
      try {
        const stream = await sendMessageStream({
          character_id: character.id,
          message: userMsg,
          conversation_id: convId || undefined,
        });
        isStreaming.current = true;
        const result = await parseSSEStream(stream, (chunk) => {
          setStreamingReply((prev) => prev + chunk);
          setThinkingStatus("");
          scrollToBottom();
        }, (status) => {
          if (status === "thinking") setThinkingStatus("AI 正在思考...");
        });
        isStreaming.current = false;

        // Save conversation_id
        if (result.conversation_id) setConvId(result.conversation_id);

        // Add AI message
        const aiMessage: Message = {
          id: result.message?.id || `msg_${Date.now()}`,
          role: "assistant",
          content: result.reply,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, aiMessage]);
        setStreamingReply("");
      } catch {
        // Fallback to non-streaming
        if (!isStreaming.current) {
          const result = await sendMessage({
            character_id: character.id,
            message: userMsg,
            conversation_id: convId || undefined,
          });
          if (result.conversation_id) setConvId(result.conversation_id);
          const aiMessage: Message = {
            id: result.message.id,
            role: "assistant",
            content: result.reply,
            timestamp: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, aiMessage]);
        }
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err_${Date.now()}`,
          role: "assistant",
          content: "抱歉，回复生成失败了。请检查 Ollama 是否正在运行。",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
      isStreaming.current = false;
      setStreamingReply("");
    }

    setTimeout(scrollToBottom, 100);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* Top bar */}
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm flex-shrink-0">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white min-w-[44px] min-h-[44px] flex items-center justify-center">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden bg-slate-800 flex-shrink-0">
          <img src={character.avatar} alt={character.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%236366f1' width='100' height='100' rx='50'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='white' font-size='40'%3E?%3C/text%3E%3C/svg%3E"; }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm sm:text-base font-semibold text-white truncate">{character.name}</h2>
          <p className="text-xs text-slate-400 truncate">{character.description}</p>
        </div>
        <div className="flex items-center gap-1">
          {/* Restart Conversation Button */}
          <div className="relative">
            {showRestartConfirm ? (
              <div className="flex items-center gap-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 animate-in fade-in">
                <span className="text-xs text-slate-300 whitespace-nowrap">重新开始？</span>
                <button
                  onClick={handleRestart}
                  className="text-xs px-2 py-0.5 bg-rose-500 hover:bg-rose-600 text-white rounded transition-colors"
                >
                  确认
                </button>
                <button
                  onClick={() => setShowRestartConfirm(false)}
                  className="text-xs px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded transition-colors"
                >
                  取消
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowRestartConfirm(true)}
                disabled={isLoading}
                className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-amber-400 disabled:opacity-40"
                title="重新对话"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
          </div>

          {onEditCharacter && (
            <button
              onClick={() => onEditCharacter(character)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-blue-400"
              title="编辑角色"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <div className="w-2 h-2 bg-green-500 rounded-full" />
          <span className="text-xs text-slate-400">在线</span>
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4 space-y-3 sm:space-y-4">
        <AnimatePresence>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div className={`flex items-start gap-2 max-w-[85%] sm:max-w-[80%] ${msg.role === "user" ? "flex-row-reverse" : ""}`}>
                {/* Avatar */}
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden bg-slate-800 flex-shrink-0 mt-1">
                    <img src={character.avatar} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {/* Message bubble */}
                <div
                  className={`px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-gradient-to-r from-rose-500 to-purple-600 text-white rounded-br-md"
                      : "bg-slate-800 text-slate-200 rounded-bl-md border border-slate-700"
                  }`}
                >
                  {msg.role === "assistant" && (
                    <p className="text-xs font-semibold text-rose-400 mb-1">{character.name}</p>
                  )}
                  <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                  {msg.role === "assistant" && msg.content.trim().length > 0 && (
                    <div className="mt-1.5 flex items-center gap-3">
                      <TTSButton text={msg.content} />
                      <TranslateButton text={msg.content} />
                    </div>
                  )}
                </div>
                {/* User avatar */}
                {msg.role === "user" && (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-1">
                    我
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Streaming output */}
        {streamingReply && (
          <div className="flex justify-start">
            <div className="flex items-start gap-2 max-w-[85%] sm:max-w-[80%]">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden bg-slate-800 flex-shrink-0 mt-1">
                <img src={character.avatar} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800 text-slate-200 border border-slate-700 text-sm leading-relaxed">
                <p className="text-xs font-semibold text-rose-400 mb-1">{character.name}</p>
                <div className="whitespace-pre-wrap break-words">
                  {streamingReply}
                  <motion.span
                    animate={{ opacity: [1, 0] }}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className="inline-block ml-1 text-rose-400"
                  >
                    |
                  </motion.span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && !streamingReply && (
          <div className="flex justify-start">
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden bg-slate-800 flex-shrink-0 mt-1">
                <img src={character.avatar} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-slate-800 border border-slate-700">
                <div className="flex gap-1">
                  <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }} className="w-2 h-2 rounded-full bg-rose-400" />
                  <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.15 }} className="w-2 h-2 rounded-full bg-rose-400" />
                  <motion.div animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.3 }} className="w-2 h-2 rounded-full bg-rose-400" />
                </div>
                {thinkingStatus && (
                  <p className="text-xs text-slate-500 mt-1">{thinkingStatus}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-slate-800 bg-slate-900/50 backdrop-blur-sm px-3 sm:px-4 py-2 sm:py-3 flex-shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`跟 ${character.name} 聊点什么...`}
            rows={1}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30 transition-all max-h-32"
            style={{ minHeight: "44px" }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = Math.min(target.scrollHeight, 128) + "px";
            }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-3 rounded-xl bg-gradient-to-r from-rose-500 to-purple-600 text-white hover:from-rose-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
        <div className="flex items-center gap-2 mt-1.5 sm:mt-2 text-xs text-slate-500">
          <AudioInput
            onTranscribed={(text) => {
              setInput((prev) => prev + text);
              setTimeout(() => inputRef.current?.focus(), 100);
            }}
          />
          <Sparkles className="w-3 h-3" />
          <span className="hidden sm:inline">Enter 发送 · Shift+Enter 换行</span>
          <span className="sm:hidden">点击发送</span>
          {convId && <span className="ml-auto">对话 #{convId.slice(-6)}</span>}
        </div>
      </div>
    </div>
  );
}
